import type { HTMLAttributes, KeyboardEventHandler, ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Os campos das telas do criador de página.
 *
 * Moravam dentro do wizard (NovaPagina). Saíram para cá quando a tela de EDIÇÃO
 * passou a ter os mesmos campos: com duas cópias, o dia em que o input ganhar um
 * estado de erro ou um foco diferente, só uma das telas ganha.
 */
export function Field({
  label,
  hint,
  error,
  required = false,
  children,
}: {
  label: string
  hint?: string
  /**
   * Lista OU texto: o Rails devolve todas as mensagens de um campo, e o Inertia
   * repassa como veio. Uma abaixo da outra empurraria o formulário — a primeira
   * é a que a pessoa precisa corrigir agora.
   */
  error?: string | string[]
  required?: boolean
  children: ReactNode
}) {
  const mensagem = Array.isArray(error) ? error[0] : error

  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-gray-700">
        {label}
        {required && (
          <span aria-hidden="true" className="text-red-500">
            {' '}
            *
          </span>
        )}
      </label>
      {children}
      {/* A dica cede lugar ao erro: as duas juntas empurram o campo seguinte e
          competem pela mesma leitura. */}
      {mensagem ? (
        <p className="text-xs text-red-600">{mensagem}</p>
      ) : (
        hint && <p className="text-xs text-gray-500">{hint}</p>
      )}
    </div>
  )
}

export function TextInput({
  value,
  onChange,
  placeholder,
  inputMode,
  invalid = false,
  onKeyDown,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode']
  invalid?: boolean
  /**
   * Existe por causa da tela de EDIÇÃO: lá os campos vivem dentro de um
   * `<form onSubmit>`, e Enter num input solto dispara o submit implícito no
   * meio da digitação. Quem precisa disso passa um handler que barra a tecla.
   */
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      inputMode={inputMode}
      aria-invalid={invalid || undefined}
      className={cn(
        'w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors',
        invalid
          ? 'border-red-300 focus:border-red-400'
          : 'border-gray-200 focus:border-gray-400',
      )}
    />
  )
}

export function TextArea({
  value,
  onChange,
  placeholder,
  rows = 4,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  rows?: number
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none transition-colors focus:border-gray-400"
    />
  )
}

/**
 * Chip de escolha múltipla — o público-alvo, no wizard e na edição.
 *
 * `aria-pressed` e não `checkbox`: são botões de verdade, e o leitor de tela
 * precisa de alguma forma de anunciar que estão marcados.
 */
export function ChoiceChip({
  label,
  selected,
  onClick,
}: {
  label: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
        selected
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-gray-200 text-gray-700 hover:border-primary/40',
      )}
    >
      {label}
    </button>
  )
}
